/**
 * The screens, menu items, tabs and buttons each permission unlocks (SC-439).
 *
 * `GET /api/rbac/catalogue` returns, for every permission, the surfaces whose `requires` is that
 * key, so the admin panel can show "this unlocks: Users tab, Invite button". Which permission a
 * surface needs is decided here, in code, on purpose: the server route behind it is guarded by the
 * same key, and moving one without the other would let the UI and the API disagree. Who holds the
 * key is data, edited through the roles and overrides APIs.
 *
 * The client references the `id`s when it hides or disables a control. A later guard test keeps
 * this list in step with the client's gating sites.
 */

import type { PermissionKey } from './catalogue'

export type SurfaceKind = 'route' | 'menu' | 'tab' | 'action'

export interface RbacSurface {
  /** Stable, `<place>.<control>` in kebab case. The client and the guard test refer to it. */
  id: string
  /** What the person sees, shown as "this unlocks <label>". */
  label: string
  kind: SurfaceKind
  requires: PermissionKey
}

export const RBAC_SURFACES: readonly RbacSurface[] = [
  {
    id: 'rooms-page.manage-rooms',
    label: 'Manage rooms',
    kind: 'action',
    requires: 'rooms.manage',
  },
  {
    id: 'rooms-dialog.restart-room',
    label: 'Restart room',
    kind: 'action',
    requires: 'rooms.manage',
  },
  {
    id: 'player-management.company-side',
    label: 'Player management for the company',
    kind: 'action',
    requires: 'rooms.manage',
  },
  { id: 'company-management.users-tab', label: 'Users tab', kind: 'tab', requires: 'members.view' },
  { id: 'manager-view.users-tab', label: 'Users tab', kind: 'tab', requires: 'members.view' },
  {
    id: 'manager-view.billing-tab',
    label: 'Billing tab',
    kind: 'tab',
    requires: 'billing.view_plan',
  },
  {
    id: 'manager-view.settings-tab',
    label: 'Settings tab',
    kind: 'tab',
    requires: 'workspace.settings.manage',
  },
  {
    id: 'company-dialog.backgrounds-tab',
    label: 'Backgrounds tab in Edit Company',
    kind: 'tab',
    requires: 'workspace.settings.manage',
  },
  {
    id: 'company-dialog.billing-tab',
    label: 'Billing tab in Edit Company',
    kind: 'action',
    requires: 'billing.view_plan',
  },
  { id: 'manager-view.wallet-tab', label: 'Wallet tab', kind: 'tab', requires: 'wallet.manage' },
  {
    id: 'manager-view.email-templates-tab',
    label: 'Email templates tab',
    kind: 'tab',
    requires: 'email_templates.manage',
  },
  {
    id: 'workspace-invite-link.card',
    label: 'Workspace invite link',
    kind: 'action',
    requires: 'members.invite',
  },
  {
    id: 'onboarding.invite-step',
    label: 'Invite step in onboarding',
    kind: 'action',
    requires: 'members.invite',
  },
  {
    id: 'invite-dialog.role-picker',
    label: 'Role when inviting someone',
    kind: 'action',
    requires: 'roles.assign',
  },
  {
    id: 'edit-user-dialog.role-picker',
    label: 'Role when editing a user',
    kind: 'action',
    requires: 'roles.assign',
  },
  {
    id: 'company-dialog.role-picker',
    label: 'Default role in the company dialog',
    kind: 'action',
    requires: 'roles.assign',
  },
  {
    id: 'domain-settings.role-picker',
    label: 'Role for domain sign-ups',
    kind: 'action',
    requires: 'roles.assign',
  },
  {
    id: 'manage-access.drawer',
    label: 'Manage access drawer',
    kind: 'action',
    requires: 'permissions.manage',
  },
  {
    id: 'roles-permissions.tab',
    label: 'Roles & permissions tab',
    kind: 'tab',
    requires: 'roles.view',
  },
  // Added with the client gating (T2 phase 5): sites the first list did not have.
  {
    id: 'manager-view.invite-button',
    label: 'Invite button in Company Management',
    kind: 'action',
    requires: 'members.invite',
  },
  {
    id: 'users-dialog.invite-button',
    label: 'Invite button in the users list',
    kind: 'action',
    requires: 'members.invite',
  },
  {
    id: 'users-dialog.edit-user',
    label: 'Edit user in the users list',
    kind: 'action',
    requires: 'members.edit',
  },
  {
    id: 'users-dialog.remove-user',
    label: 'Remove user in the users list',
    kind: 'action',
    requires: 'members.remove',
  },
  {
    id: 'users-dialog.transfer-ownership',
    label: 'Transfer ownership',
    kind: 'action',
    requires: 'workspace.transfer_ownership',
  },
  // Added with the blocked-state pass (SC-439 phase F): requests the screen made whether or not
  // the person could make them.
  {
    id: 'users-dialog.billing-status',
    label: 'Billing status in the users list',
    kind: 'action',
    requires: 'billing.view_plan',
  },
  {
    id: 'users-dialog.pending-users',
    label: 'Pending users (awaiting payment) in the users list',
    kind: 'action',
    // Cloud's getPendingUsers, cancelPendingUser, the payment step and the addition preview all
    // ask for members.invite, the key of the Invite button beside it; billing.view_plan only
    // guards the credit balance, which that dialog hides on its own.
    requires: 'members.invite',
  },
  {
    id: 'users-dialog.sync-unassigned',
    label: 'Sync unassigned users in the users list',
    kind: 'action',
    requires: 'members.invite',
  },
]

/** The surfaces a key unlocks, in the order they are listed above. */
export function surfacesUnlockedBy(key: string): RbacSurface[] {
  return RBAC_SURFACES.filter((surface) => surface.requires === key)
}
